import { z } from "zod";

/**
 * Per-tool policy: what happens before a tool call runs.
 *
 * `allow` runs unattended, `deny` refuses with a structured tool result, and
 * `approve` parks the conversation on a durable approval row until a
 * workspace member resolves it. There is one waiting mode: every gated call
 * is a row, whoever ends up resolving it.
 */
export const TOOL_ACTIONS = ["allow", "approve", "deny"] as const;
export const toolActionSchema = z.enum(TOOL_ACTIONS);
export type ToolAction = z.infer<typeof toolActionSchema>;

export const DEFAULT_APPROVAL_TIMEOUT_HOURS = 24;
export const MAX_APPROVAL_TIMEOUT_HOURS = 168;

export const toolOverrideSchema = z.object({
	action: toolActionSchema,
	/** Member user ids allowed to resolve; empty means any workspace member. */
	approvers: z.array(z.string().min(1).max(128)).max(64).default([]),
	/** The profile author's note, shown to the approver as trusted text. */
	note: z.string().max(500).optional(),
	/** `{arg}` placeholders over the call's arguments; rendered as plain text. */
	preview: z.string().max(300).optional(),
	timeoutHours: z
		.number()
		.int()
		.min(1)
		.max(MAX_APPROVAL_TIMEOUT_HOURS)
		.default(DEFAULT_APPROVAL_TIMEOUT_HOURS),
});
export type ToolOverride = z.infer<typeof toolOverrideSchema>;

export const toolPolicySchema = z.object({
	defaultAction: toolActionSchema.default("allow"),
	overrides: z.record(z.string().min(1).max(128), toolOverrideSchema).default({}),
});
export type ToolPolicy = z.infer<typeof toolPolicySchema>;

export const DEFAULT_TOOL_POLICY: ToolPolicy = { defaultAction: "allow", overrides: {} };

/** What a policy that cannot be read becomes: nothing runs unattended. */
export const UNREADABLE_TOOL_POLICY: ToolPolicy = { defaultAction: "deny", overrides: {} };

/**
 * Read a stored (jsonb) policy. No policy means allow-all; a policy that
 * exists but does not parse fails closed, since whatever the author wrote
 * may well have been a restriction.
 */
export function parseStoredToolPolicy(value: unknown): ToolPolicy {
	if (value === null || value === undefined) return DEFAULT_TOOL_POLICY;
	const parsed = toolPolicySchema.safeParse(value);
	return parsed.success ? parsed.data : UNREADABLE_TOOL_POLICY;
}

/** True when any tool under this policy would not simply run. */
export function policyRestrictsTools(policy: ToolPolicy | undefined): boolean {
	if (!policy) return false;
	if (policy.defaultAction !== "allow") return true;
	return Object.values(policy.overrides).some((override) => override.action !== "allow");
}

export function resolveToolAction(policy: ToolPolicy, toolName: string): ToolAction {
	return policy.overrides[toolName]?.action ?? policy.defaultAction;
}

export function resolveToolOverride(policy: ToolPolicy, toolName: string): ToolOverride | null {
	return policy.overrides[toolName] ?? null;
}

/** Tool names whose calls park for approval under this policy. */
export function gatedToolNames(policy: ToolPolicy): string[] {
	return Object.entries(policy.overrides)
		.filter(([, override]) => override.action === "approve")
		.map(([name]) => name);
}

function stringifyArgument(value: unknown): string {
	if (value === null || value === undefined) return "";
	if (typeof value === "string") return value;
	if (typeof value === "number" || typeof value === "boolean") return String(value);
	try {
		return JSON.stringify(value);
	} catch {
		return String(value);
	}
}

function lookupPath(args: Record<string, unknown>, path: string): unknown {
	let current: unknown = args;
	for (const segment of path.split(".")) {
		if (current === null || typeof current !== "object") return undefined;
		current = (current as Record<string, unknown>)[segment];
	}
	return current;
}

/**
 * Render the author's preview template over the call's arguments. Values are
 * substituted as plain text (the caller renders the result inertly); a
 * placeholder with no matching argument is left as written so the approver
 * can see it was not supplied.
 */
export function renderApprovalPreview(
	template: string | undefined,
	args: Record<string, unknown>,
): string | null {
	if (!template || template.trim().length === 0) return null;
	const rendered = template.replace(/\{([a-zA-Z0-9_.]+)\}/g, (match, path: string) => {
		const value = lookupPath(args, path);
		return value === undefined ? match : stringifyArgument(value);
	});
	return rendered.trim().length > 0 ? rendered : null;
}
